import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Container,
    AppBar,
    Toolbar,
    IconButton,
    Tabs,
    Tab,
    Alert,
    Paper
} from "@mui/material";
import {
    Logout
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import AdminGuard from "../components/AdminGuard";
import AdminOpportunitiesTable from "../components/AdminOpportunitiesTable";
import OpportunityApprovals from "../components/OpportunityApprovals";
import ValidationView from "../modules/admin/ValidationView";
import TeamAssignment from "../modules/admin/TeamAssignment";

const AdminPanel = () => {
    const { currentUser, isAdmin, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const tabs = [
        { label: t("admin.opportunities", "Oportunidades"), value: 0 },
        { label: t("admin.approvals", "Aprobaciones"), value: 1 },
        { label: t("admin.validation", "Validación"), value: 2 },
        { label: t("admin.teamAssignment", "Asignación de Equipos"), value: 3 },
        { label: t("admin.legalTracking", "Seguimiento Legal"), value: 4 }
    ];

    useEffect(() => {
        if (!isAdmin) {
            navigate("/dashboard");
        }
    }, [isAdmin, navigate]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate("/login");
        } catch (error) {
            // Handle logout error silently
        }
    };

    const handleRefresh = () => {
        // Refresh functionality can be implemented here
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <AdminGuard>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                flexGrow: 1,
                                background: "linear-gradient(90deg, #DBC078 0%, #d0ad50 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                fontWeight: 900,
                            }}
                        >
              CAPIVIO - Panel de Administración
                        </Typography>
                        <Typography variant="body1" sx={{ mr: 2 }}>
                            {currentUser?.email}
                        </Typography>
                        <LanguageSwitcher />
                        <IconButton color="inherit" onClick={handleLogout}>
                            <Logout />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                        <Typography variant="h3" sx={{ fontWeight: 900, color: "#155263" }}>
              Panel de Administración
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate("/admin/agentes")}
                            sx={{ ml: 2 }}
                        >
                            {t("agents.manageButton", "Gestión de Agentes (Captadores)")}
                        </Button>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                    {/* Tabs Navigation */}
                    <Paper sx={{ mb: 3 }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange} 
                            aria-label="admin-panel-tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {tabs.map((tab) => (
                                <Tab key={tab.value} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>

                    {/* Tab Content */}
                    <Box sx={{ mt: 3 }}>
                        {activeTab === 0 && (
                            <AdminOpportunitiesTable 
                                onOpportunitySelect={() => {}}
                                onRefresh={handleRefresh}
                                showFilters={true}
                                maxHeight={600}
                            />
                        )}
                        {activeTab === 1 && (
                            <OpportunityApprovals setError={setError} setSuccess={setSuccess} />
                        )}
                        {activeTab === 2 && (
                            <ValidationView />
                        )}
                        {activeTab === 3 && (
                            <TeamAssignment />
                        )}
                        {activeTab === 4 && (
                            <Box>
                                <Typography variant="h5" sx={{ mb: 3 }}>
                  Seguimiento Legal
                                </Typography>
                                <Alert severity="info">
                                    <Typography>
                    Módulo de seguimiento legal en desarrollo. Aquí se mostrará el estado de validación legal de todas las oportunidades.
                                    </Typography>
                                </Alert>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </AdminGuard>
    );
};

export default AdminPanel;